/**
 * Enhanced textarea component with built-in validation
 */

import React, { useEffect } from 'react'
import type { FieldValidationConfig } from '../types'
import { useFieldValidation } from '../hooks/useFieldValidation'

interface ValidatedTextareaProps {
  name: string
  label?: string
  value: string
  onChange: (name: string, value: string) => void
  onValidation?: (name: string, isValid: boolean, message?: string) => void
  validationConfig?: FieldValidationConfig
  placeholder?: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  className?: string
  rows?: number
  maxLength?: number
  showCharacterCount?: boolean
}

export const ValidatedTextarea: React.FC<ValidatedTextareaProps> = ({
  name,
  label,
  value,
  onChange,
  onValidation,
  validationConfig = {},
  placeholder,
  helpText,
  required,
  disabled,
  className = '',
  rows = 4,
  maxLength,
  showCharacterCount = false
}) => {
  const { validationState, validateField } = useFieldValidation({
    required,
    ...validationConfig
  })

  // Validate required fields on mount and when they become empty
  useEffect(() => {
    if (required) {
      const result = validateField(value)
      onValidation?.(name, result.isValid, result.message)
    }
  }, [required, name, validateField, onValidation, value])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    
    // Respect maxLength if provided
    if (maxLength && newValue.length > maxLength) {
      return
    }
    
    onChange(name, newValue)
    
    // Validate on change if configured or if field was already validated
    if (validationConfig.validateOnChange || validationState.isValidated) {
      const result = validateField(newValue)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const handleBlur = () => {
    // Always validate on blur (default behavior)
    if (validationConfig.validateOnBlur !== false) {
      const result = validateField(value)
      onValidation?.(name, result.isValid, result.message)
    }
  }

  const getTextareaClassName = () => {
    let textareaClass = `validated-textarea ${className}`
    
    if (validationState.isValidated) {
      textareaClass += validationState.isValid ? ' valid' : ' invalid'
    }
    
    return textareaClass
  }

  const showValidationIcon = validationConfig.showSuccessIcon !== false && validationState.isValidated
  const characterCount = value.length
  const isNearMaxLength = maxLength && characterCount > maxLength * 0.8

  return (
    <div className="validated-textarea-wrapper">
      {label && (
        <label htmlFor={name} className="validated-textarea-label">
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}
      
      <div className="validated-textarea-container">
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          rows={rows}
          maxLength={maxLength}
          className={getTextareaClassName()}
          aria-invalid={validationState.isValidated && !validationState.isValid}
          aria-describedby={
            (validationState.message && `${name}-error`) || 
            (helpText && `${name}-help`) || 
            undefined
          }
        />
        
        {showValidationIcon && (
          <div className="validation-icon">
            {validationState.isValid ? (
              <i className="bi bi-check-circle text-success" aria-hidden="true"></i>
            ) : (
              <i className="bi bi-exclamation-circle text-danger" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
      
      {(showCharacterCount || isNearMaxLength) && maxLength && (
        <div className={`character-count ${isNearMaxLength ? 'warning' : ''}`}>
          {characterCount}/{maxLength} characters
        </div>
      )}
      
      {validationState.isValidated && validationState.message && (
        <div 
          id={`${name}-error`}
          className="validation-message error"
          role="alert"
          aria-live="polite"
        >
          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>
          {validationState.message}
        </div>
      )}
      
      {helpText && !validationState.message && (
        <div id={`${name}-help`} className="help-text">
          {helpText}
        </div>
      )}
    </div>
  )
}
